import { useMemo, useState } from "react";
import { AddCreditWrapper } from "../styles";
import {
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { SnitcherSettings } from "types/snitcherSettings";
import { PatchSnitcherSettingsInputs } from "hooks/services/subscription/usePatchSnitcherSettings";

interface IProps {
  close: () => void;
  onSubmit: (
    inputs: Omit<PatchSnitcherSettingsInputs, "subscriptionId">,
  ) => void;
  snitcherSettings: SnitcherSettings | null;
  isLoading?: boolean;
}

const EditSnitcherApiKeyModal = ({
  close,
  onSubmit,
  snitcherSettings,
  isLoading,
}: IProps) => {
  const [apiKey, setApiKey] = useState<string>("");
  const [workspaceUuid, setWorkspaceUuid] = useState<string>(
    snitcherSettings?.workspaceUuid ?? "",
  );

  const trimmedApiKey = apiKey.trim();
  const trimmedWorkspaceUuid = workspaceUuid.trim();

  const allGood = useMemo(() => {
    if (!trimmedWorkspaceUuid) return false;

    return (
      trimmedApiKey.length > 0 ||
      trimmedWorkspaceUuid !== snitcherSettings?.workspaceUuid
    );
  }, [trimmedApiKey, trimmedWorkspaceUuid, snitcherSettings]);

  const handleSubmit = () => {
    if (!allGood) return;
    // An empty api key would clear the stored one, so it is only sent when set.
    onSubmit({
      workspaceUuid: trimmedWorkspaceUuid,
      ...(trimmedApiKey.length > 0 && { apiKey: trimmedApiKey }),
    });
  };

  return (
    <AddCreditWrapper id="add-credit-wrapper" onClick={close}>
      <div
        className="container"
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        }}
        style={{ gap: 12 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontSize={24} lineHeight={"30px"} fontWeight={700}>
            Set Snitcher Settings
          </Typography>
          <Close style={{ cursor: "pointer" }} onClick={close} />
        </div>
        <Typography fontSize={12} lineHeight={"18px"}>
          With its own API key the subscription resolves visitors through its own
          Snitcher account. Without one it falls back on Crono's internal
          Snitcher integration. The workspace uuid must belong to the same
          Snitcher account as the key, otherwise Snitcher replies not found.
        </Typography>
        {snitcherSettings?.hasOwnApiKey && (
          <Typography fontSize={12} lineHeight={"18px"} fontWeight={600}>
            This subscription already has its own API key — leave the field
            empty to keep it, or type a new one to replace it.
          </Typography>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Typography fontSize={12} fontWeight={600}>
            Workspace Uuid
          </Typography>
          <TextField
            id="snitcherWorkspaceUuid"
            name="snitcherWorkspaceUuid"
            value={workspaceUuid}
            type="text"
            onChange={(event) => setWorkspaceUuid(event.target.value)}
            placeholder="Snitcher workspace uuid"
            autoComplete="off"
            variant="standard"
            fullWidth
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Typography fontSize={12} fontWeight={600}>
            API Key
          </Typography>
          <TextField
            id="snitcherApiKey"
            name="snitcherApiKey"
            value={apiKey}
            type="password"
            onChange={(event) => setApiKey(event.target.value)}
            placeholder="Snitcher API key"
            autoComplete="new-password"
            variant="standard"
            fullWidth
          />
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!allGood}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </AddCreditWrapper>
  );
};

export default EditSnitcherApiKeyModal;
